import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import {
    Plus, Search, FileText, ChevronLeft, ChevronRight,
    ChevronsLeft, ChevronsRight, ArrowUpDown, Filter, RefreshCw, Box, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import StatusBadge from './StatusBadge';

const PRList = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    const { canCreate } = usePermissions('pr');
    const [showAllSites, setShowAllSites] = useState(false);
    const fontContext = useFontSize();
    const fontSize = fontContext?.fontSize || 13;

    // --- State الأساسي ---
    const [prs, setPrs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const session = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionSite = session.site_id || '';
    const sessionOrg = session.org_id || '';

    // --- Params State (URL Persistence) ---
    const searchParams = new URLSearchParams(location.search);
    const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
    const [limit] = useState(50);
    const [sort, setSort] = useState({
        column: searchParams.get('sortBy') || 'prnum',
        direction: searchParams.get('sortDir') || 'desc'
    });

    // --- 🎯 فلاتر الأعمدة ---
    const [filters, setFilters] = useState({
        prnum: searchParams.get('prnum') || "",
        description: searchParams.get('description') || "",
        status: searchParams.get('status') || "",
        siteid: searchParams.get('siteid') || ""
    });

    const fetchData = useCallback(async (isAll = showAllSites) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/pr', {
                params: {
                    page,
                    limit,
                    all_sites: Boolean(isAll),
                    user_siteid: sessionSite,
                    orgid: sessionOrg,
                    sortBy: sort.column,
                    sortDir: sort.direction,
                    prnum: filters.prnum,
                    description: filters.description,
                    status: filters.status,
                    filter_siteid: filters.siteid
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setPrs(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            toast.error(t('pr.messages.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [page, limit, sort, filters, showAllSites, sessionSite, sessionOrg, t]);

    // Debounced Effect for Search/Filters
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchData();
        }, 400);
        return () => clearTimeout(timer);
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const handleRowClick = (prnum) => {
        const queryParams = new URLSearchParams({
            page,
            ...filters,
            sortBy: sort.column,
            sortDir: sort.direction,
            all_sites: showAllSites,
            user_siteid: sessionSite,
            orgid: sessionOrg
        }).toString();
        navigate(`/pr/${prnum}?${queryParams}`);
    };

    const handleSort = (column) => {
        setSort(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({ prnum: "", description: "", status: "", siteid: "" });
        setPage(1);
    };

    // Global Events for Multi-Site Toggle
    useEffect(() => {
        const openAllSites = () => { setShowAllSites(true); setPage(1); };
        const viewMySite = () => { setShowAllSites(false); setPage(1); };
        window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
        window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        return () => {
            window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
            window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
        };
    }, []);

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div
            className={`flex flex-col h-full bg-surface-app text-[11px] uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right dir-rtl' : 'font-sans text-left'}`}
            style={{ fontSize: `${fontSize}px` }}
            dir={isRTL ? 'rtl' : 'ltr'}
        >

            {/* Toolbar */}
            <div className="bg-surface-card p-3 border-b shadow-sm z-20">
                <div className={`flex justify-between items-center px-4`}>
                    <h2 className={`text-primary-dark font-black italic uppercase text-lg flex items-center gap-2`}>
                        <FileText size={22} className="text-primary" />
                        {t('pr.headers.prList')}
                        <RefreshCw
                            size={18}
                            className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary hover:text-primary'} ${isRTL ? 'mr-2' : 'ml-2'}`}
                            onClick={() => fetchData()}
                        />
                    </h2>

                    <div className={`flex gap-3`}>
                        <button onClick={clearFilters} className="flex items-center gap-2 px-4 py-2 text-ink-secondary hover:text-status-danger font-bold transition-colors">
                            <X size={16}/> {t('pr.actions.clearFilters')}
                        </button>
                        {canCreate && (
                            <button onClick={() => navigate('/pr/new')} className={`bg-primary-dark text-white px-8 py-2 rounded-sm font-black flex items-center gap-2 hover:bg-primary shadow-lg active:scale-95 transition-all`}>
                                <Plus size={16} /> {t('pr.actions.newPR')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table Area */}
            <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                <div className="bg-surface-card border rounded-sm shadow-sm overflow-hidden">
                    <table className={`w-full border-collapse ${isRTL ? 'text-right' : 'text-left'}`}>
                        <thead className="bg-primary-dark text-white font-bold uppercase sticky top-0 z-10">
                            <tr className={isRTL ? 'text-right' : 'text-left'}>
                                <SortableHeader label={t('pr.tables.prNum')} sortKey="prnum" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <SortableHeader label={t('pr.tables.description')} sortKey="description" currentSort={sort} onSort={handleSort} isRTL={isRTL} />
                                <th className="p-3 border-x border-white/10 text-center">{t('pr.tables.status')}</th>
                                <SortableHeader label={t('pr.tables.totalCost')} sortKey="totalcost" currentSort={sort} onSort={handleSort} align="right" isRTL={isRTL} />
                                <SortableHeader label={t('pr.tables.reqDate')} sortKey="requireddate" currentSort={sort} onSort={handleSort} align="center" isRTL={isRTL} />
                                <th className="p-3 text-center border-x border-white/10">{t('pr.tables.site')}</th>
                            </tr>
                            {/* Filter Row */}
                            <tr className={`bg-surface-panel shadow-inner`}>
                                <td className="p-1 border-x border-line">
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.prnum} onChange={e => handleFilterChange('prnum', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-line">
                                    <input className={`w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} value={filters.description} onChange={e => handleFilterChange('description', e.target.value)} />
                                </td>
                                <td className="p-1 border-x border-line">
                                    <select className={`w-full p-1.5 text-primary font-black outline-none border border-line-subtle bg-surface-card ${isRTL ? 'dir-rtl text-right' : ''}`} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
                                        <option value="">{t('common.all')}</option>
                                        <option value="WAPPR">WAPPR</option>
                                        <option value="APPR">APPR</option>
                                        <option value="CLOSE">CLOSE</option>
                                        <option value="CAN">CANCELLED</option>
                                    </select>
                                </td>
                                <td className="p-1 border-x border-line bg-surface-panel"></td>
                                <td className="p-1 border-x border-line bg-surface-panel"></td>
                                <td className="p-1 border-x border-line">
                                    <input className="w-full p-1.5 text-primary font-bold outline-none border border-line-subtle focus:border-primary bg-surface-card text-center" placeholder={t('common.filter')} value={filters.siteid} onChange={e => handleFilterChange('siteid', e.target.value)} />
                                </td>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {loading ? (
                                <tr><td colSpan="6" className="p-20 text-center font-black text-ink-secondary animate-pulse uppercase tracking-[0.2em]">{t('pr.messages.syncing')}</td></tr>
                            ) : prs.length > 0 ? (
                                prs.map((pr, i) => (
                                    <tr key={i} className={`hover:bg-primary-soft cursor-pointer transition-colors group ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`} onClick={() => handleRowClick(pr.prnum)}>
                                        <td className="p-3 border-x font-black text-primary group-hover:underline">{pr.prnum}</td>
                                        <td className="p-3 border-x text-ink font-bold">{pr.description}</td>
                                        <td className="p-3 border-x text-center">
                                            <StatusBadge status={pr.status} />
                                        </td>
                                        <td className={`p-3 border-x font-mono text-status-success font-black italic bg-surface-panel ${isRTL ? 'text-left' : 'text-right'}`}>
                                            {parseFloat(pr.totalcost || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                        </td>
                                        <td className="p-3 border-x text-center text-ink-secondary font-bold">{pr.requireddate?.split('T')[0]}</td>
                                        <td className="p-3 text-center border-x text-ink-secondary font-black">{pr.siteid}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="p-20 text-center font-black text-ink-secondary italic uppercase">{t('pr.messages.noData')}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Hybrid Pagination Footer */}
            <div className={`bg-primary-dark text-white p-2 px-8 flex justify-between items-center shadow-inner z-30 font-black uppercase tracking-widest text-[10px] ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-6`}>
                    <div className={`flex items-center gap-2`}>
                        <Box size={14} className="text-primary"/>
                        <span>{t('pr.footer.showing')}: <span className="text-primary mx-1">{(page-1)*limit+1}-{Math.min(page*limit, total)}</span></span>
                    </div>
                    <div className={`flex items-center gap-2 border-white/20 px-6 ${isRTL ? 'border-r' : 'border-l'}`}>
                        <Filter size={14} className="text-status-warning"/>
                        <span>{t('pr.footer.totalRecords')}: <span className="text-status-warning mx-1">{total}</span></span>
                    </div>
                </div>

                <div className={`flex items-center gap-4`}>
                    <div className={`flex items-center gap-1 bg-black/20 rounded px-2 py-1 border border-white/10 `}>
                        <button disabled={page === 1} onClick={() => setPage(1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronLeft size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <div className={`px-4 text-[11px] font-mono border-x border-white/10 mx-1 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <span className="text-status-warning font-black text-sm">{page}</span>
                            <span className="text-ink-secondary text-[9px]">{t('common.of')} {totalPages}</span>
                        </div>
                        <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                        <button disabled={page >= totalPages} onClick={() => setPage(totalPages)} className="p-1.5 hover:text-status-warning disabled:opacity-20 transition-all"><ChevronsRight size={18} className={isRTL ? 'rotate-180' : ''}/></button>
                    </div>
                    <RefreshCw size={16} className={`cursor-pointer transition-all ${loading ? 'animate-spin text-status-warning' : 'text-primary hover:text-white'}`} onClick={() => fetchData()} />
                </div>
            </div>
        </div>
    );
};

// --- Helpers ---
const SortableHeader = ({ label, sortKey, currentSort, onSort, align = "left", isRTL }) => {
    // تحديد المحاذاة مع مراعاة اللغة
    let justifyClass = 'justify-start';
    if (align === 'center') justifyClass = 'justify-center';
    if (align === 'right') justifyClass = isRTL ? 'justify-start' : 'justify-end';
    if (align === 'left' && isRTL) justifyClass = 'justify-end';

    return (
        <th className={`p-3 border-x border-white/10 cursor-pointer hover:bg-primary transition-colors ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => onSort(sortKey)}>
            <div className={`flex items-center gap-2 ${justifyClass} ${isRTL ? 'flex-row-reverse' : ''}`}>
                {label}
                <ArrowUpDown size={12} className={currentSort.column === sortKey ? 'text-status-warning' : 'text-white/30'} />
            </div>
        </th>
    );
};

export default PRList;
